import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  eatsKeys,
  eatsQueries,
  orderApi,
  type ListBannersParams,
  type ListMerchantPromotionsParams,
  type ListMerchantsParams,
  type ListOrdersParams,
  type ListProductCategoriesParams,
  type ListRestaurantCategoriesParams,
  type ListMerchantProductsByRestaurantParams,
  type ListMerchantProductsParams,
  type ListPromotionsByRestaurantParams,
  type ListRestaurantsParams,
  type OrderDateRange,
  type PaginationParams,
} from '../api';
import type { MerchantDecision, PreparationStage } from './common';

// Thin read hooks wrapping the `queryOptions` factories, plus the two order
// transitions the merchant owns. Richer mutations (product/category/promotion
// writes) belong one layer up in `features`, colocated with their forms — these
// live here because they are pure entity-state transitions with no UI of their
// own, and they simply invalidate the affected order queries.

// ── Merchants ────────────────────────────────────────────────────────────────
export const useMerchantCapabilities = () => useQuery(eatsQueries.merchantCapabilities());
/**
 * The merchant's realtime channels. `enabled` gates the fetch because only the
 * screens that actually open the websocket need it.
 */
export const useRealtimeChannels = (enabled = true) =>
  useQuery({ ...eatsQueries.merchantRealtimeChannels(), enabled });
export const useMerchants = (params?: ListMerchantsParams) =>
  useQuery(eatsQueries.merchantList(params));
export const useMerchant = (id: string) => useQuery(eatsQueries.merchant(id));

// ── Restaurants (branches) ───────────────────────────────────────────────────
export const useRestaurants = (params?: ListRestaurantsParams) =>
  useQuery(eatsQueries.restaurantList(params));
export const useRestaurant = (id: string) => useQuery(eatsQueries.restaurant(id));
export const useRestaurantDeliveryPricing = (id: string) =>
  useQuery(eatsQueries.restaurantDeliveryPricing(id));
export const useRestaurantCategories = (params?: ListRestaurantCategoriesParams) =>
  useQuery(eatsQueries.restaurantCategoryList(params));

// ── Products ─────────────────────────────────────────────────────────────────
export const useProducts = (params?: ListMerchantProductsParams) =>
  useQuery(eatsQueries.productList(params));
export const useProductsByRestaurant = (
  id: string,
  params?: ListMerchantProductsByRestaurantParams,
) => useQuery(eatsQueries.productsByRestaurant(id, params));
export const useTopProducts = (params: { limit?: number }) =>
  useQuery(eatsQueries.topProducts(params));
export const useProduct = (id: string) => useQuery(eatsQueries.product(id));
export const useProductByRestaurant = (id: string, restaurantId: string) =>
  useQuery(eatsQueries.productByRestaurant(id, restaurantId));

// ── Product categories ───────────────────────────────────────────────────────
export const useProductCategories = (params?: ListProductCategoriesParams) =>
  useQuery(eatsQueries.productCategoryList(params));
export const useProductCategory = (id: string) =>
  useQuery(eatsQueries.productCategory(id));

// ── Promotions ───────────────────────────────────────────────────────────────
export const useMerchantPromotions = (params?: ListMerchantPromotionsParams) =>
  useQuery(eatsQueries.merchantPromotions(params));
export const useRestaurantPromotions = (
  restaurantId: string,
  params?: ListPromotionsByRestaurantParams,
) => useQuery(eatsQueries.restaurantPromotions(restaurantId, params));
/** One promotion at one branch — the read requires a branch (see the query). */
export const usePromotion = (id: string, restaurantId: string) =>
  useQuery(eatsQueries.promotion(id, restaurantId));

// ── Orders ───────────────────────────────────────────────────────────────────
export const useMerchantOrders = (params?: ListOrdersParams) =>
  useQuery(eatsQueries.merchantOrders(params));
export const useRestaurantOrders = (restaurantId: string, params?: ListOrdersParams) =>
  useQuery(eatsQueries.restaurantOrders(restaurantId, params));
export const useOrder = (id: string, restaurantId?: string) =>
  useQuery(eatsQueries.order(id, restaurantId));
export const useMerchantWeekdayCounts = (range: OrderDateRange) =>
  useQuery(eatsQueries.merchantWeekdayCounts(range));
export const useRestaurantWeekdayCounts = (restaurantId: string, range: OrderDateRange) =>
  useQuery(eatsQueries.restaurantWeekdayCounts(restaurantId, range));

/**
 * Accept or reject a pending order. No optimistic patch: the server decides
 * the resulting status (a decision can be refused once the deadline lapses), so
 * the authoritative response is written into the cache and the lists refetch.
 */
export function useRespondToOrder(orderId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (decision: MerchantDecision) => orderApi.respond(orderId, decision),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: eatsKeys.orders.all });
    },
  });
}

/** Advance an accepted order's kitchen stage, then refresh the order queries. */
export function useUpdateOrderPreparationStage(orderId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (stage: PreparationStage) =>
      orderApi.updatePreparationStage(orderId, stage),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: eatsKeys.orders.all });
    },
  });
}

// ── Reviews ──────────────────────────────────────────────────────────────────
export const useMerchantReviews = (params?: PaginationParams) =>
  useQuery(eatsQueries.merchantReviews(params));
export const useRestaurantReviews = (restaurantId: string, params?: PaginationParams) =>
  useQuery(eatsQueries.restaurantReviews(restaurantId, params));

// ── Banners ──────────────────────────────────────────────────────────────────
export const useRestaurantBanners = (params?: ListBannersParams) =>
  useQuery(eatsQueries.restaurantBannerList(params));
export const useRestaurantBanner = (id: string) =>
  useQuery(eatsQueries.restaurantBanner(id));
export const useProductBanners = (params?: ListBannersParams) =>
  useQuery(eatsQueries.productBannerList(params));
export const useProductBanner = (id: string) => useQuery(eatsQueries.productBanner(id));
